import _                                                                                                                      from 'underscore'
import React, { FC, useRef, }                                                                                                 from 'react';
import { BarElement, CategoryScale, Chart, Chart as ChartJS, ChartOptions, Legend, LinearScale, Title, Tooltip, TooltipItem } from 'chart.js';
import { Bar }                                                                                                                from 'react-chartjs-2';
import { Table }                                                                                                              from 'semantic-ui-react';

import { formatCurrency, getArrColumnTotals, mergeDeep, percentage, roundToPrecision } from '../../../../../../../util';
import { CorvalProject, Uuid }                                                         from '../../../../../../../types';
import { Charting }                                                                    from '../../../../../../../types/charting';
import { ChartingService, LangService }                                                from '../../../../../../../services';
import { BudgetCostTypesByProjectCostTypes }                                           from '../../../../../../../services/ChartingService';
import { ProjectQueries }                                                              from '../../../../../../../queries/ProjectQueries';
import { ACTIVITY_STATUS }                                                             from '../../../../../../../constants/ProductEnums';
import { EMPTY_VALUE_DISPLAY }                                                         from '../../../../../../../constants/FormatStringEnums';
import { ItemFromQuery }                                                               from '../../../../../../components/from-query/ItemFromQuery';
import { SmallGearLoadingMessage }                                                     from '../../../../../../components/util/loading/SmallGearLoadingMessage';
import { useBudgetCostTypesByProjectChartReportExport }                                from '../../../../../../../hooks/reporting/chart-reports/useBudgetCostTypesByProjectChartReportExport';
import { ChartReportWithControlsLayout }                                               from '../ChartReportWithControlsLayout';

ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
	ChartingService.chartJSPluginLegendAdjustHeight( 0 )
);

type ReportChartingType = Charting.Reports.BudgetCostTypesByProject.ChartingType;
type ReportChartingData = Charting.Reports.BudgetCostTypesByProject.ChartingData;

interface Props
	extends Charting.Reporting.ChartReportBaseProps<ReportChartingType>,
		Charting.Reporting.ChartReportingViewProps<ReportChartingType>,
		Charting.Reporting.ChartReportingExportProps<ReportChartingType> {
	
	// Project IDs to retrieve data on
	projectIds?: Uuid[],
	
	// Primary project to compare on
	primaryProjectId?: Uuid | null,
	
	// Primary project's term dictionary to use as basis for terminology
	termDictionary?: null,
	
	// What cost types should be displayed
	costTypes?: BudgetCostTypesByProjectCostTypes[],
}

const defaultProps: Props = {
	projectIds:          null,
	costTypes:           [
		BudgetCostTypesByProjectCostTypes.ACTIVITY_COSTS,
		BudgetCostTypesByProjectCostTypes.HIRE_COSTS,
		BudgetCostTypesByProjectCostTypes.CUSTOM_COSTS
	],
	primaryProjectId:    null,
	title:               'Project Budgets By Cost Type',
	showTitle:           true,
	renderAsChart:       true,
	chartOptions:        {},
	enableTableToggle:   false,
	enableImageDownload: false,
	enableCSVDownload:   false,
	enableCopyDataText:  false,
	chartViewIcon:       'chart bar outline',
	datapointFormatter:  ( value: number ) => {
		return ( value > 0 ) ? formatCurrency( value, false ) : EMPTY_VALUE_DISPLAY;
	},
	itemFromQueryProps:  {
		loadingComponent: () => <SmallGearLoadingMessage label={ 'Loading Project Budget Data...' }/>
	}
}

const BudgetCostTypesByProject: FC<Props> = ( props ) => {
	
	const chartRef      = useRef<Chart<ReportChartingType>>(),
	      exportMethods = useBudgetCostTypesByProjectChartReportExport( { chartRef, ...props } );
	
	const processData = ( data: CorvalProject[] ) => {
		
		const includedCostTypes = props.costTypes;
		const filterMethod      = null;
		
		return ChartingService.budgetCostTypesByProjectChartingData( data, filterMethod, LangService.getDictionary( props.termDictionary ), includedCostTypes );
	}
	
	const getChartOptions = (): ChartOptions<ReportChartingType> => {
		
		return mergeDeep( {
			plugins:    {
				title:   {
					text:    props.title,
					display: props.showTitle,
					padding: {
						bottom: 20
					},
					
				},
				legend:  {
					position: 'bottom',
					
					// hack for no-text title element to provide padding
					// distance from chart when positioned on bottom
					title: {
						display: true,
						padding: {
							top: 20
						}
					},
					
					onHover( event ){
						//@ts-ignore
						event.native.target.style.cursor = 'pointer';
					},
					onLeave( event ){
						//@ts-ignore
						event.native.target.style.cursor = 'default';
					}
				},
				tooltip: {
					
					...ChartingService.getDefaultChartJSTooltipFormatOptions(),
					
					callbacks: {
						label: formatLabel
					}
				},
				
			},
			responsive: true,
			scales:     {
				x: { stacked: false },
				y: { stacked: false, ticks: { callback: formatChartYTick } },
			},
			
		} as ChartOptions<ReportChartingType> )
	}
	
	const formatChartYTick = ( value, index, ticks ) => {
		
		return formatDatapoint( value );
	}
	
	const formatDatapoint = ( datapoint ) => {
		
		return props.datapointFormatter ? props.datapointFormatter( datapoint ) : datapoint;
	}
	
	const formatLabel = ( tooltipItem: TooltipItem<any> ) => {
		
		const projectName    = tooltipItem.dataset.label,
		      value          = tooltipItem.parsed.y,
		      totalCost      = _.last( tooltipItem.dataset.data ),
		      formattedValue = formatDatapoint( value ),
		      percentOfTotal = roundToPrecision( percentage( value, totalCost ), 2 );
		
		// If showing percent, assumes at least 3 indices in dataset.data and that the last one is the total
		return ( value != totalCost && tooltipItem.dataset.data.length > 1 )
		       ? `${ projectName }: ${ formattedValue } (${ percentOfTotal }%)`
		       : `${ projectName }: ${ formattedValue }`;
	}
	
	return (
		
		<ItemFromQuery
			as={ React.Fragment }
			query={ ProjectQueries.queries.GET_PROJECTS( `
				id
                name
                budget(
                includeRecurrenceActivities: true,
                completionStateIn:           [
	                ${ ACTIVITY_STATUS.NOTSTARTED },
	                ${ ACTIVITY_STATUS.INCOMPLETE },
	                ${ ACTIVITY_STATUS.COMPLETE },
                ]
                ){
                feeBasedCostOccurrences{
                    costOccurrence
                    costAmount
                    costAmountType
                    instanceType
                }
                resourceCostOccurrences{
                    resourceCostOccurrence
                    quarterlyCompensation
                }
            }` ) }
			queryOptions={ {
				variables: {
					where: {
						and: [
							{ id: { in: props.projectIds } },
							{ isActive: { eq: true } }
						]
					}
				}
			} }
			useLoadingComponent={ true }
			queryResultObject={ ( data ) => {
				
				const projects = data.projects as CorvalProject[];
				
				return processData( projects );
				
			} }
			{ ...props.itemFromQueryProps }>
			
			{ ( data: ReportChartingData ) => {
				
				return (
					
					<ChartReportWithControlsLayout<ReportChartingType, ReportChartingData>
						
						{ ...props }
						
						data={ data }
						exportMethods={ exportMethods }
						chartComponent={
							
							<Bar ref={ chartRef } options={ getChartOptions() } data={ data }/>
						}
						tableComponent={
							
							<Table basic={ 'very' } size={ 'small' } striped definition>
								
								<Table.Header>
									<Table.Row>
										
										<Table.HeaderCell>Project</Table.HeaderCell>
										
										{/* Cost Type Labels */ }
										{ data.labels.map( ( label, index ) => <Table.HeaderCell key={ index }>{ label }</Table.HeaderCell> ) }
									
									</Table.Row>
								</Table.Header>
								<Table.Body>
									
									{ data.datasets.map( ( dataset: Charting.ExtendedChartData<ReportChartingType> ) => {
										
										return (
											
											<Table.Row key={ dataset.label }>
												
												{/* Project Name Label */ }
												<Table.Cell>
													<>{ dataset.label }</>
												</Table.Cell>
												
												{/* Cost Type Totals */ }
												{ dataset.data.map( ( datapointValue, index ) => (
													<Table.Cell key={ `${ dataset.label }-${ index }-${ datapointValue }` }>
														{ formatDatapoint( datapointValue ) }
													</Table.Cell>
												) ) }
											
											</Table.Row>
										)
									} ) }
								</Table.Body>
								<Table.Footer>
									
									<Table.Row>
										
										<Table.Cell></Table.Cell>
										
										{/* Y Column Axes Totals */ }
										{ getArrColumnTotals( data.datasets.map( dataset => dataset.data ) ).map( ( datapointValue, index ) => (
											
											<Table.Cell key={ index }>
												<strong>{ formatDatapoint( datapointValue ) }</strong>
											</Table.Cell>
										
										) ) }
									
									</Table.Row>
								</Table.Footer>
							</Table>
						}
					/>
				)
			} }
		
		
		</ItemFromQuery>
	
	);
}

BudgetCostTypesByProject.defaultProps = defaultProps

export { BudgetCostTypesByProject };
